import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Car, DollarSign, Percent, Users, FileText, Settings, 
  Shield, Download, LogOut, Bug, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const SuperAdminDashboard = () => {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('earnings');
  const [earnings, setEarnings] = useState({});
  const [commission, setCommission] = useState({ 
    platform_fee_percentage: 15,
    renter_fee_percent: 10,
    host_fee_percent: 5
  });
  const [admins, setAdmins] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantUserId, setGrantUserId] = useState('');
  const [newCommission, setNewCommission] = useState({
    total: 15,
    renter: 10,
    host: 5
  });
  const [savingCommission, setSavingCommission] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'earnings') {
        const res = await api.get('/super-admin/earnings');
        setEarnings(res.data);
      } else if (activeTab === 'commission') {
        const res = await api.get('/super-admin/commission');
        setCommission(res.data);
        setNewCommission({
          total: res.data.platform_fee_percentage || 15,
          renter: res.data.renter_fee_percent || 10,
          host: res.data.host_fee_percent || 5
        });
      } else if (activeTab === 'admins') {
        const res = await api.get('/super-admin/admins');
        setAdmins(res.data.admins || []);
      } else if (activeTab === 'audit') {
        const res = await api.get('/super-admin/audit-logs?limit=100');
        setAuditLogs(res.data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCommission = async () => {
    // Validate split sums to total
    if (Math.abs((newCommission.renter + newCommission.host) - newCommission.total) > 0.01) {
      toast.error(`Renter (${newCommission.renter}%) + Host (${newCommission.host}%) must equal Total (${newCommission.total}%)`);
      return;
    }
    
    setSavingCommission(true);
    try {
      await api.put('/super-admin/commission', { 
        platform_fee_percentage: newCommission.total,
        renter_fee_percent: newCommission.renter,
        host_fee_percent: newCommission.host
      });
      toast.success('Commission updated - will apply to new bookings only');
      setCommission({ 
        ...commission, 
        platform_fee_percentage: newCommission.total,
        renter_fee_percent: newCommission.renter,
        host_fee_percent: newCommission.host
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update commission');
    } finally {
      setSavingCommission(false);
    }
  };

  const handleGrantAdmin = async () => {
    if (!grantUserId.trim()) return;
    try {
      await api.post(`/super-admin/admins/${grantUserId}/grant`);
      toast.success('Admin role granted');
      setShowGrantModal(false);
      setGrantUserId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant admin role');
    }
  };

  const handleRevokeAdmin = async (userId) => {
    try {
      await api.post(`/super-admin/admins/${userId}/revoke`);
      toast.success('Admin role revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke admin role');
    }
  };

  const handleExportAuditLogs = async () => {
    try {
      const res = await api.get('/super-admin/audit-logs/export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('Audit logs exported');
    } catch (error) {
      toast.error('Failed to export audit logs');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#004B8D] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6 text-[#004B8D]" />
              </div>
            </Link>
            <div>
              <h1 className="font-heading text-xl font-semibold">Super Admin</h1>
              <p className="text-sm text-white/70">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="secondary" size="sm">Admin Panel</Button>
            </Link>
            <Link to="/dev-debug">
              <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
                <Bug className="w-4 h-4 mr-2" />
                Dev Debug
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
            <TabsTrigger value="commission">Commission</TabsTrigger>
            <TabsTrigger value="admins">Admin Users</TabsTrigger>
            <TabsTrigger value="audit">Audit Logs</TabsTrigger>
            <TabsTrigger value="legal">Legal Pages</TabsTrigger>
          </TabsList>

          {/* Earnings Tab */}
          <TabsContent value="earnings">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-slate-100">
                <CardContent className="p-6">
                  <DollarSign className="w-8 h-8 text-green-600 mb-4" />
                  <p className="text-3xl font-bold text-slate-900">
                    ${earnings.total_platform_earnings?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-slate-500">Platform Earnings</p>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="p-6">
                  <DollarSign className="w-8 h-8 text-[#004B8D] mb-4" />
                  <p className="text-3xl font-bold text-slate-900">
                    ${earnings.total_revenue?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-slate-500">Total Revenue</p>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="p-6">
                  <FileText className="w-8 h-8 text-[#00A3BF] mb-4" />
                  <p className="text-3xl font-bold text-slate-900">
                    {earnings.total_bookings_paid || 0}
                  </p>
                  <p className="text-slate-500">Paid Bookings</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Commission Tab */}
          <TabsContent value="commission">
            <Card className="border-slate-100 max-w-lg">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Percent className="w-5 h-5 text-[#004B8D]" />
                  Platform Commission - Split Fee Model
                </CardTitle>
                <CardDescription>
                  Set the platform fee split between renter and host. Changes apply to new bookings only.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Settings Display */}
                <div className="bg-slate-50 p-4 rounded-xl space-y-2">
                  <p className="text-sm font-medium text-slate-600">Current Fee Structure</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-white p-3 rounded-lg border">
                      <p className="text-xs text-slate-500">Total</p>
                      <p className="text-lg font-bold text-slate-900">{commission.platform_fee_percentage}%</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600">Renter Pays</p>
                      <p className="text-lg font-bold text-blue-700">{commission.renter_fee_percent || 10}%</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-green-200">
                      <p className="text-xs text-green-600">Host Pays</p>
                      <p className="text-lg font-bold text-green-700">{commission.host_fee_percent || 5}%</p>
                    </div>
                  </div>
                </div>
                
                {/* Edit Form */}
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Total Fee %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="0.5"
                        value={newCommission.total}
                        onChange={(e) => {
                          const total = parseFloat(e.target.value) || 0;
                          // Auto-adjust split maintaining ratio
                          const renterRatio = newCommission.renter / (newCommission.renter + newCommission.host) || 0.7;
                          setNewCommission({
                            total,
                            renter: Math.round(total * renterRatio * 100) / 100,
                            host: Math.round(total * (1 - renterRatio) * 100) / 100
                          });
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-blue-600">Renter Fee %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="0.5"
                        value={newCommission.renter}
                        onChange={(e) => setNewCommission({
                          ...newCommission,
                          renter: parseFloat(e.target.value) || 0,
                          total: (parseFloat(e.target.value) || 0) + newCommission.host
                        })}
                        className="mt-1 border-blue-200"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-green-600">Host Fee %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="0.5"
                        value={newCommission.host}
                        onChange={(e) => setNewCommission({
                          ...newCommission,
                          host: parseFloat(e.target.value) || 0,
                          total: newCommission.renter + (parseFloat(e.target.value) || 0)
                        })}
                        className="mt-1 border-green-200"
                      />
                    </div>
                  </div>
                  
                  {Math.abs((newCommission.renter + newCommission.host) - newCommission.total) > 0.01 && (
                    <p className="text-sm text-red-600 bg-red-50 p-2 rounded">
                      ⚠️ Renter ({newCommission.renter}%) + Host ({newCommission.host}%) must equal Total ({newCommission.total}%)
                    </p>
                  )}
                  
                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Fee changes only apply to new bookings. Existing bookings keep their original fee snapshot.
                    </p>
                  </div>
                </div>
                
                <Button 
                  onClick={handleSaveCommission}
                  disabled={savingCommission || Math.abs((newCommission.renter + newCommission.host) - newCommission.total) > 0.01}
                  className="bg-[#004B8D] hover:bg-[#003865] w-full"
                >
                  {savingCommission ? 'Saving...' : 'Update Commission'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <Card className="border-slate-100">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Admin Users</CardTitle>
                <Button onClick={() => setShowGrantModal(true)} className="bg-[#004B8D] hover:bg-[#003865]">
                  Grant Admin Role
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {admins.map((admin) => {
                      const isSuperAdmin = admin.roles?.some(r => r.role === 'super_admin' && r.is_active);
                      const isAdmin = admin.roles?.some(r => r.role === 'admin' && r.is_active);
                      
                      return (
                        <div key={admin.user_id} className="flex items-center justify-between p-4 border border-slate-200 rounded-xl">
                          <div>
                            <p className="font-medium">{admin.full_name}</p>
                            <p className="text-sm text-slate-500">{admin.email}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex gap-1">
                              {isSuperAdmin && (
                                <Badge className="bg-purple-100 text-purple-800">Super Admin</Badge>
                              )}
                              {isAdmin && !isSuperAdmin && (
                                <Badge className="bg-blue-100 text-blue-800">Admin</Badge>
                              )}
                            </div>
                            {isAdmin && !isSuperAdmin && (
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => handleRevokeAdmin(admin.user_id)}
                              >
                                Revoke
                              </Button>
                            )}
                            {isSuperAdmin && (
                              <Badge className="bg-green-100 text-green-800">Protected</Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="audit">
            <Card className="border-slate-100">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Audit Logs</CardTitle>
                <Button variant="outline" onClick={handleExportAuditLogs}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
                  </div>
                ) : auditLogs.length === 0 ? (
                  <p className="text-center py-12 text-slate-500">No audit logs</p>
                ) : (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {auditLogs.map((log) => (
                      <div key={log.log_id} className="p-3 bg-slate-50 rounded-lg text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="outline">{log.action}</Badge>
                          <span className="text-xs text-slate-500">
                            {new Date(log.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-slate-600">
                          {log.entity_type}: {log.entity_id?.slice(0, 12)}
                        </p>
                        <p className="text-xs text-slate-500">By: {log.user_email}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Legal Pages Tab */}
          <TabsContent value="legal">
            <div className="grid md:grid-cols-3 gap-6">
              {['terms', 'privacy', 'insurance'].map((page) => (
                <Link key={page} to={`/legal/${page}?edit=true`}>
                  <Card className="border-slate-100 hover:shadow-md transition-shadow cursor-pointer h-full">
                    <CardContent className="p-6 flex items-center justify-between">
                      <div>
                        <FileText className="w-6 h-6 text-[#004B8D] mb-2" />
                        <p className="font-medium capitalize">{page === 'terms' ? 'Terms of Service' : page === 'privacy' ? 'Privacy Policy' : 'Insurance Info'}</p>
                        <p className="text-sm text-slate-500">Click to edit</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Grant Admin Modal */}
      <Dialog open={showGrantModal} onOpenChange={setShowGrantModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Admin Role</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>User ID</Label>
            <Input
              value={grantUserId}
              onChange={(e) => setGrantUserId(e.target.value)}
              placeholder="Enter user_id..."
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-2">
              Find the user_id from the Users tab in Admin Panel
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGrantModal(false)}>Cancel</Button>
            <Button onClick={handleGrantAdmin} className="bg-[#004B8D] hover:bg-[#003865]">
              Grant Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
